import { Component, OnInit, effect, inject, input, model, output, signal } from '@angular/core';
import { DataService } from '../../services/data.service';

// What the guest picked, as opposed to the resulting amount - lets a host restore the
// choice (and recalculate a percentage against a changed total) when the form re-mounts.
export type TipSelection = { percentageIndex: number } | { customAmount: number };

@Component({
  selector: 'app-tipping-form',
  templateUrl: './tipping-form.component.html',
  styleUrl: './tipping-form.component.scss',
  standalone: false,
})
export class TippingFormComponent implements OnInit {
  private dataService = inject(DataService);

  total = input.required<number>();
  selection = input<TipSelection | null>(null);
  tip = model<number | null>(null);
  tipApplied = output<{ tip_amount: number; selection: TipSelection | null }>();
  tippingConfig = this.dataService.tippingConfig;
  selectedIndex = signal<number | null>(null);

  constructor() {
    effect(() => {
      const total = this.total();
      const index = this.selectedIndex();
      if (index === null) return;

      const option = this.tippingConfig()?.options[index];
      if (!option) return;

      const percentage = parseFloat(option.percentage);
      if (isNaN(percentage) || percentage < 0) return;

      const calculated = parseFloat(((total * percentage) / 100).toFixed(2));
      this.tipApplied.emit({ tip_amount: calculated, selection: { percentageIndex: index } });
    });
  }

  ngOnInit(): void {
    const selection = this.selection();
    if (!selection) return;

    if ('percentageIndex' in selection) {
      this.selectedIndex.set(selection.percentageIndex);
    } else {
      this.tip.set(selection.customAmount);
    }
  }

  applyTip(): void {
    this.selectedIndex.set(null);

    if (!this.tip() || this.tip()! < 0) {
      this.tip.set(null);
    }

    if (this.tip()! > 100000) {
      this.tip.set(100000);
    }

    const amount = this.tip();
    this.tipApplied.emit({ tip_amount: amount ?? 0, selection: amount ? { customAmount: amount } : null });
  }

  applyPercentageTip(percentage: string, index: number): void {
    this.tip.set(null);

    if (this.selectedIndex() === index) {
      this.selectedIndex.set(null);
      this.tipApplied.emit({ tip_amount: 0, selection: null });
      return;
    }

    const parsedPercentage = parseFloat(percentage);

    if (isNaN(parsedPercentage) || parsedPercentage < 0) return;

    this.selectedIndex.set(index);
    const calculated = parseFloat(((this.total() * parsedPercentage) / 100).toFixed(2));
    this.tipApplied.emit({ tip_amount: calculated, selection: { percentageIndex: index } });
  }
}
